import classnames from "classnames";
import * as I from "immutable";
import moment from "moment-timezone";
import Raven from "raven-js";
import React, {Component} from "react";
import PropTypes from "prop-types";

import {DATE_FORMAT_ISO, SATURDAY_TAGS_CATEGORY, SATURDAY_TAGS_REASON} from "config/constants";
import {trackEvent} from "metrics";
import {getDateFormat, getRouteLabel, isDataLoaded} from "utils";

import api from "v2/api";
import {apiDestination, getDestination} from "v2/api/Destinations";
import {apiError} from "v2/api/Errors";
import {AppData, Route} from "v2/api/Records";
import {apiRoute} from "v2/api/Routes";

import DestinationAddress from "v2/components/DestinationAddress";
import DestinationTitle from "v2/components/DestinationTitle";
import Empty from "v2/components/Empty";
import FieldError from "v2/components/FieldError";
import FieldGroup from "v2/components/FieldGroup";
import Loading from "v2/components/Loading";
import ServerError from "v2/components/ServerError";
import ModalForm from "v2/components/responsive/ModalForm";

import styles from "v2/styles";

export default class DuplicateDestinationForm extends Component {
  static contextTypes = {
    appData: PropTypes.instanceOf(AppData),
    location: PropTypes.object,
    onUpdateRouteData: PropTypes.func,
    params: PropTypes.shape({
      date: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      routeId: PropTypes.string.isRequired,
    }),
    routeData: PropTypes.instanceOf(Route),
    router: PropTypes.object,
  }
  static defaultProps = {
    action: "duplicate",
    ignoreRouteId: false,
  }
  static propTypes = {
    action: PropTypes.string.isRequired,
    ignoreRouteId: PropTypes.bool.isRequired,
    modal: PropTypes.bool,
    onCancel: PropTypes.func,
  }

  state = {
    data: null,
    dataError: null,
    errors: I.Map(),
    newRouteId: null,
    submitted: false,
    updating: false,
  }

  componentDidMount() {
    this.loadData();
  }

  handleRouteChange = (evt) => {
    this.setState({
      newRouteId: evt.target.value,
    });
  }

  handleCategoryChange = (evt) => {
    this.setState({category: evt.target.value});
  }

  handlePaidChange = (evt) => {
    this.setState({paid: evt.target.value});
  }

  handleReasonChange = (evt) => {
    this.setState({reason: evt.target.value});
  }

  handleSubmit = (evt) => {
    evt.preventDefault();
    this.setState({
      submitted: true,
    });

    const {
      location: {query},
      params: {date, id, routeId},
      onUpdateRouteData,
      router,
    } = this.context;
    const {action} = this.props;
    const {modal, onCancel} = this.props;

    api()
      .post(this.apiUrl, {
        route: this.newRouteId,
      })
      .then(({data}) => {
        this.setState({
          updating: true,
        });

        const destination = apiDestination(data);
        const newDate = destination.route.date.format(DATE_FORMAT_ISO);
        const newId = destination.id.toString();
        const newRouteId = destination.route.id.toString();

        onUpdateRouteData(null, () => {
          const newContext = {
            ...this.context,
            params: {
              date: newDate,
              id: newId,
              routeId: newRouteId,
            },
          };
          onUpdateRouteData(newContext, () => {
            if (modal) {
              onCancel();
            }
            trackEvent(
              `${action} Destination`,
              ["Destination", action, newId],
              {
                "Old Date": date,
                "Old ID": id,
                "Old Route ID": routeId,
                "New Date": newDate,
                "New ID": newId,
                "New Route ID": newRouteId,
              });
            router.push({
              pathname: `/${newDate}/${newRouteId}/${newId}`,
              query,
            });
          });
        });
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            errors: I.Map(err.response.data),
            submitted: false,
          });
        } else {
          Raven.captureException(err);
        }
      });
  };

  get apiUrl() {
    const {params: {routeId, id}} = this.context;
    const {action} = this.props;
    return `/routes/${routeId}/destinations/${id}/${action}`;
  }

  get dataByType() {
    const {appData: {serviceType}} = this.context;
    const {data} = this.state;
    return data.groupBy(item => {
      return `${item.date.format(getDateFormat())} - ${serviceType.get(item.type)}`;
    });
  }

  loadData() {
    const {params: {routeId: routeIdStr}, routeData: {type: routeType}} = this.context;
    const {ignoreRouteId} = this.props;
    const routeId = parseInt(routeIdStr, 10);

    api()
      .get("/routes", {
        params: {
          "date__gte": moment().format(DATE_FORMAT_ISO),
          "ordering": "date,title",
          "kind": routeType,
          "_": moment().unix(),
        },
      })
      .then((response) => {
        const data = I.List(response
          .data
          .map(apiRoute)
          .filter(item => {
            return ignoreRouteId ? item.id !== routeId : true;
          }));
        this.setState({data});
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            dataError: apiError(err.response.data),
          });
        } else {
          Raven.captureException(err);
        }
      });
  }

  get newRouteId() {
    const {params: {routeId}} = this.context;
    const {ignoreRouteId} = this.props;
    const {newRouteId} = this.state;

    if (newRouteId) {
      return newRouteId;
    }

    return ignoreRouteId ?
      this.dataByType.first().first().id.toString() :
      routeId;
  }

  renderSaturdayFields() {
    const {routeData} = this.context;
    const {category, data, paid, reason} = this.state;

    const labelClassName = "col-xs-12 col-sm-3 control-label";
    const fieldContainerClassName = "col-xs-12 col-sm-9";

    const newRoute = data && data.find(r => String(r.id) === String(this.newRouteId));

    // Saturday tags are only required if moving a destination from a
    // weekday maintenance route to Saturday
    if (
      !newRoute || routeData.type !== "MAI" ||
      routeData.date.day() === 6 || newRoute.date.day() !== 6
    ) {
      return;
    }

    return [
      <FieldGroup key="reason">
        <label className={labelClassName}>Reason:</label>
        <div className={fieldContainerClassName}>
          <select
            className="form-control"
            onChange={this.handleReasonChange}
            style={{width: "auto"}}
            value={reason}>
            <option value="">-</option>
            {SATURDAY_TAGS_REASON.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>
      </FieldGroup>,

      <FieldGroup key="paid">
        <label className={labelClassName}>Paid ?:</label>
        <div className={fieldContainerClassName}>
          <label>
            <input
              checked={paid === "Paid"}
              name="paid"
              onChange={this.handlePaidChange}
              type="radio"
              value="Paid" />
            <span className="erg-margin-left-10">Yes</span>
          </label><br />
          <label>
            <input
              checked={paid === "Unpaid"}
              name="paid"
              onChange={this.handlePaidChange}
              type="radio"
              value="Unpaid" />
            <span className="erg-margin-left-10">No</span>
          </label>
        </div>
      </FieldGroup>,

      <FieldGroup key="category">
        <label className={labelClassName}>Category:</label>
        <div className={fieldContainerClassName}>
          <select
            className="form-control"
            onChange={this.handleCategoryChange}
            style={{width: "auto"}}
            value={category}>
            <option value="">-</option>
            {SATURDAY_TAGS_CATEGORY.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>
      </FieldGroup>,
    ];
  }

  renderContent() {
    const {params: {id}, routeData} = this.context;
    const {data, dataError, errors, updating} = this.state;

    if (updating) {
      return <Loading />;
    }

    const destination = getDestination(routeData, id);
    if (!destination) {
      return null;
    }

    if (dataError) {
      return <ServerError data={dataError} />;
    } else if (data && data.isEmpty()) {
      return (
        <Empty>
          No possible target routes found. Cannot duplicate destination.
        </Empty>
      );
    }

    const labelClassName = "col-xs-12 col-sm-3 control-label";
    const fieldContainerClassName = "col-xs-12 col-sm-9";

    let routeContent;
    if (data === null) {
      routeContent = <Loading inForm />;
    } else {
      routeContent = (
        <select
          className="form-control"
          onChange={this.handleRouteChange}
          value={this.newRouteId}>
          {this.dataByType.map((items, label) => (
            <optgroup key={`group-${label}`} label={label}>
              {items.map(item => (
                <option key={`route-${item.id}`} value={item.id}>
                  {getRouteLabel(item, true)}
                </option>
              )).toArray()}
            </optgroup>
          )).toArray()}
        </select>
      );
    }

    let nonFieldErrors;
    if (errors.get("nonFieldErrors")) {
      nonFieldErrors = (
        <div className={classnames(fieldContainerClassName, "col-sm-offset-3", "has-error")}>
          <FieldError>{errors.get("nonFieldErrors")}</FieldError>
        </div>
      );
    }

    return (
      <div className="form-horizontal">
        <div className="erg-margin-bottom-15 small">
          <DestinationTitle
            data={destination}
            style={{fontSize: styles.FONT_SIZE.LARGE}} /><br />
          <DestinationAddress crmLink data={destination} />
        </div>

        <FieldGroup error={errors.get("route")}>
          <label className={labelClassName}>Route:</label>
          <div className={fieldContainerClassName}>
            {routeContent}
            <FieldError>{errors.get("route")}</FieldError>
          </div>
        </FieldGroup>

        {this.renderSaturdayFields()}
        {nonFieldErrors}
      </div>
    );
  }

  render() {
    const {submitted} = this.state;
    return (
      <ModalForm
        btnSubmitDisabled={submitted || !isDataLoaded(this.state)}
        btnSubmitLabel="Duplicate"
        modalTitle="Duplicate Destination"
        onSubmit={this.handleSubmit}
        pageTitle="Duplicate Destination"
        {...this.props}>
        {this.renderContent()}
      </ModalForm>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/app/v2/components/responsive/DuplicateDestinationForm.js
 **/